import {hexToRgba} from "./html/html";
import {StyleUtils} from "./styleUtils";

interface ISpacing {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  padding?: number;
  margin?: number;
  horizontal?: number;
  vertical?: number;
}

/**
 * Хелперы для прямых CSS свойств
 */
export class CssStyleHelpers {

static aspectRatio(aspectRatio: number) {
  return {
    aspectRatio: `${aspectRatio}`,
  };
}

static borderRadius(borderRadius: number, unit: string = 'px') {
  if (!borderRadius) return { borderRadius: '0px' };
  
  // Якщо значення 50%, повертаємо 50% для створення повністю круглої форми
  if (unit === '%' && borderRadius >= 50) return { borderRadius: '50%' };
  
  return {
    borderRadius: `${borderRadius}${unit}`
  };
}

static paddings(indents: ISpacing | number) {

  if(typeof indents === 'number') {
    return {
      padding: `${indents}px`
    };
  }

  // Приоритет: индивидуальные значения > вертикальные/горизонтальные > общий padding
  const paddingTop = indents?.top || indents?.vertical || indents?.padding || 0;
  const paddingBottom = indents?.bottom || indents?.vertical || indents?.padding || 0;
  const paddingLeft = indents?.left || indents?.horizontal || indents?.padding || 0;
  const paddingRight = indents?.right || indents?.horizontal || indents?.padding || 0;

  return {
    paddingTop: paddingTop > 0 ? `${paddingTop}px` : '0px',
    paddingBottom: paddingBottom > 0 ? `${paddingBottom}px` : '0px',
    paddingLeft: paddingLeft > 0 ? `${paddingLeft}px` : '0px',
    paddingRight: paddingRight > 0 ? `${paddingRight}px` : '0px'
  };
}

static margins(indents: ISpacing) {
  // Приоритет: индивидуальные значения > вертикальные/горизонтальные > общий margin
  const marginTop = indents?.top || indents?.vertical || indents?.margin || 0;
  const marginBottom = indents?.bottom || indents?.vertical || indents?.margin || 0;
  const marginLeft = indents?.left || indents?.horizontal || indents?.margin || 0;
  const marginRight = indents?.right || indents?.horizontal || indents?.margin || 0;
  
  return {
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    marginLeft: `${marginLeft}px`,
    marginRight: `${marginRight}px`
  };
}

static border(
  borderOptions: { 
    width?: number; 
    color?: string; 
    style?: string;
    borderLeft?: number | { width?: number; color?: string; style?: string } | string;
    borderRight?: number | { width?: number; color?: string; style?: string } | string;
    borderTop?: number | { width?: number; color?: string; style?: string } | string;
    borderBottom?: number | { width?: number; color?: string; style?: string } | string;
  }, 
  isShown: boolean = true
) {
  if (!isShown) {
    return {
      border: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderTop: 'none',
      borderBottom: 'none'
    };
  }

  // Если заданы отдельные стороны, применяем их
  const hasSpecificSides = borderOptions?.borderLeft !== undefined || 
                           borderOptions?.borderRight !== undefined || 
                           borderOptions?.borderTop !== undefined || 
                           borderOptions?.borderBottom !== undefined;

  if (hasSpecificSides) {
    const result: Record<string, string> = {};
    const defaultColor = borderOptions?.color;
    const defaultStyle = borderOptions?.style || 'solid';
    const defaultWidth = borderOptions?.width || 1;

    // Функция для обработки значения стороны
    const processSide = (sideValue: number | { width?: number; color?: string; style?: string } | string | undefined) => {
      if (sideValue === undefined) return undefined;
      
      // Если строка - возвращаем как есть
      if (typeof sideValue === 'string') {
        return sideValue;
      }
      
      // Если число - это ширина
      if (typeof sideValue === 'number') {
        if (sideValue <= 0) return undefined;
        // Если цвет не передан или пустой, бордер не отображается
        if (!defaultColor || typeof defaultColor !== 'string' || defaultColor.trim() === '') return undefined;
        return `${sideValue}px ${defaultStyle} ${defaultColor}`;
      }
      
      // Если объект
      if (typeof sideValue === 'object') {
        const width = sideValue.width ?? defaultWidth;
        const color = sideValue.color ?? defaultColor;
        const style = sideValue.style ?? defaultStyle;
        if (width <= 0) return undefined;
        // Если цвет не передан или пустой, бордер не отображается
        if (!color || typeof color !== 'string' || color.trim() === '') return undefined;
        return `${width}px ${style} ${color}`;
      }
      
      return undefined;
    };

    const borderLeftValue = borderOptions.borderLeft !== undefined ? processSide(borderOptions.borderLeft) : undefined;
    if (borderLeftValue !== undefined) {
      result.borderLeft = borderLeftValue;
    }
    
    const borderRightValue = borderOptions.borderRight !== undefined ? processSide(borderOptions.borderRight) : undefined;
    if (borderRightValue !== undefined) {
      result.borderRight = borderRightValue;
    }
    
    const borderTopValue = borderOptions.borderTop !== undefined ? processSide(borderOptions.borderTop) : undefined;
    if (borderTopValue !== undefined) {
      result.borderTop = borderTopValue;
    }
    
    const borderBottomValue = borderOptions.borderBottom !== undefined ? processSide(borderOptions.borderBottom) : undefined;
    if (borderBottomValue !== undefined) {
      result.borderBottom = borderBottomValue;
    }

    return result;
  }
  
  // Старая логика для обратной совместимости
  if (!borderOptions?.width || borderOptions?.width <= 0) {
    return {
      border: 'none'
    };
  }
  
  const { width, color, style = 'solid' } = borderOptions;
  
  return {
    border: `${width}px ${style} ${color || '#000000'}`
  };
}


// Хелпер для создания стилей overlay (блюр, тіні тощо)
static blur(textOverlayBlur: number, isShown: boolean = true) {
  return {
    backdropFilter: isShown ? `blur(${textOverlayBlur}px)` : 'none'
  };
}


/**
 * Создает стиль фона с поддержкой градиентов, угла поворота, массива цветов и других параметров
 * @param backgroundColor - Цвет фона (строка) или объект с параметрами градиента
 * @param isShown - Показывать ли фон
 * @returns CSS стили для фона
 */
static background(
  backgroundColor: string | {
    type?: 'solid' | 'linear' | 'radial' | 'conic';
    color?: string;
    colors?: Array<string | { color: string; position?: number; alpha?: number }>;
    angle?: number;
    direction?: 'to top' | 'to bottom' | 'to left' | 'to right' | 'to top right' | 'to top left' | 'to bottom right' | 'to bottom left';
    center?: { x?: string | number; y?: string | number };
    shape?: 'circle' | 'ellipse';
    size?: 'closest-side' | 'closest-corner' | 'farthest-side' | 'farthest-corner' | string;
    position?: string;
  },
  isShown: boolean = true
) {
  if (!isShown) return {};
  
  // Если передан объект с параметрами
  if (typeof backgroundColor === 'object' && backgroundColor !== null) {
    const config = backgroundColor;
    const type = config.type || 'solid';
    
    // Простой цвет
    if (type === 'solid' || (!config.colors && config.color)) {
      const color = config.color || '';
      const isDefaultBlack = StyleUtils.isDefaultBlackColor(color);
      return {
        background: color && !isDefaultBlack ? color : 'transparent'
      };
    }
    
    // Линейный градиент
    if (type === 'linear' && config.colors && config.colors.length > 0) {
      const gradientStr = StyleUtils.createLinearGradient({
        colors: config.colors,
        angle: config.angle,
        direction: config.direction
      });
      return { background: gradientStr };
    }
    
    // Радиальный градиент
    if (type === 'radial' && config.colors && config.colors.length > 0) {
      const gradientStr = StyleUtils.createRadialGradient({
        colors: config.colors,
        shape: config.shape,
        size: config.size,
        center: config.center,
        position: config.position
      });
      return { background: gradientStr };
    }
    
    // Конический градиент
    if (type === 'conic' && config.colors && config.colors.length > 0) {
      const gradientStr = StyleUtils.createConicGradient({
        colors: config.colors,
        angle: config.angle,
        center: config.center,
        position: config.position
      });
      return { background: gradientStr };
    }
  }
  
  // Если передан простой цвет (строка)
  if (typeof backgroundColor === 'string') {
    const isDefaultBlack = StyleUtils.isDefaultBlackColor(backgroundColor);
    return {
      background: backgroundColor && !isDefaultBlack ? backgroundColor : 'transparent'
    };
  }
  
  return {};
}

// Хелпер для фонового зображення
static backgroundImage(imageUrl: string, isShown: boolean = true) {
  if (!isShown || !imageUrl) return {};
  
  return {
    backgroundImage: `url(${imageUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };
}


static filterShadow(shadow: any) {
  if (!shadow || !shadow.enabled) {
    return { filter: 'none' };
  }

  if (!shadow.enabled || shadow.blur === 0) {
    return { filter: 'none' };
  }

  // Используем значение по умолчанию для opacity, если оно не задано
  const opacity = shadow.opacity !== undefined ? shadow.opacity : 0.3;
  const color = shadow.color || '#000000';
  const offsetX = shadow.offsetX || 0;
  const offsetY = shadow.offsetY || 4;
  const blur = shadow.blur || 10;

  const rgbaColor = StyleUtils.colorToRgba(color, opacity);

  return {
    filter: `drop-shadow(${offsetX}px ${offsetY}px ${blur}px ${rgbaColor})`
  };
}

static flexDirection(flexDirection: boolean) {
  return {
    flexDirection: flexDirection ? 'row-reverse' : 'row'
  };
}

// Універсальний хелпер для grid - колонки на PC, рядки на мобільних
static grid(columns: number) {
  return { 
    gridTemplateColumns: `repeat(${columns}, 1fr)` 
  };
}

// Хелпер для відступів між елементами
static gap(gap: number) {
  return {
    gap: `${gap}px`
  };
}

static alignItems(align: string = 'center') {
  return {
    alignItems: align as 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline'
  }
}

static color(color: string) {
  return {
    color: color || '#000000'
  }
}

static buildSvgImageStyle(
  path: string,
  textColor: string,
  isUseCustomMarkerColor?: boolean,
  markerBackgroudColor?: string,
  markerColor?: string,
): Record<string, string | number> {
  const base = {
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    flexShrink: 0,
    minWidth: '40px',
  };
  if (isUseCustomMarkerColor) {
    const customBg = (markerBackgroudColor || markerColor || '').trim();
    if (customBg) {
      return {
        ...base,
        background: customBg,
        WebkitMask: `url(${path}) center/contain no-repeat`,
        mask: `url(${path}) center/contain no-repeat`,
        color: textColor,
      };
    }
  }
  return {
    ...base,
    backgroundImage: `url(${path})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    color: textColor,
  };
}

}

// Реэкспорт для обратной совместимости
export { StyleUtils } from "./styleUtils";

export class CssImageHelpers {
  static imageProps(image: any) {
    // Читаємо containerPreset якщо він є, інакше використовуємо окремі властивості (зворотна сумісність)
    const containerPreset = image?.containerPreset;
    const containerWidth = containerPreset?.width ?? image?.containerWidth ?? null;
    const containerHeight = containerPreset?.height ?? image?.containerHeight ?? null;
    const aspectRatio = containerPreset?.aspectRatio ?? image?.aspectRatio ?? null;

    return {
      type: image?.type,
      imageSrc: image?.previewUrl || image?.url || null,
      width: image?.width || null,
      height: image?.height || null,
      aspectRatio: aspectRatio,
      containerWidth: containerWidth,
      containerHeight: containerHeight,
      containerPreset: containerPreset, // Додаємо повний об'єкт containerPreset
      zoom: image?.zoom || 100, // Додаємо підтримку зуму
      positionX: image?.positionX || 0, // Позиція X
      positionY: image?.positionY || 0, // Позиція Y
    };
  }
}

export class CssVideoHelpers {
  static videoProps(video: any) {
    // Читаємо containerPreset якщо він є, інакше використовуємо окремі властивості (зворотна сумісність)
    const containerPreset = video?.containerPreset;
    const containerWidth = containerPreset?.width ?? video?.containerWidth ?? null;
    const containerHeight = containerPreset?.height ?? video?.containerHeight ?? null;
    const aspectRatio = containerPreset?.aspectRatio ?? video?.aspectRatio ?? null;

    return {
      type: video?.type,
      videoSrc: video?.previewUrl || video?.url || null,
      width: video?.width || null,
      height: video?.height || null,
      aspectRatio: aspectRatio,
      containerWidth: containerWidth,
      containerHeight: containerHeight,
      containerPreset: containerPreset, // Додаємо повний об'єкт containerPreset
      zoom: video?.zoom || 100, // Додаємо підтримку зуму
      positionX: video?.positionX || 0, // Позиція X
      positionY: video?.positionY || 0, // Позиція Y
    };
  }
}

